/**
 * Boot screen (reference 01-boot): wordmark, progress, the live Output Log,
 * and a Resume.pdf download that is always visible.
 *
 * Everything shown is read from engine stores: log lines from the Log,
 * progress and status from the BootMonitor. Nothing is scripted (rule 7).
 */
import { useMemo } from "react";
import type { Log, LogEntry, Verbosity } from "../../core/Log";
import { formatLogTime } from "../format/numbers";
import { useStore } from "../hooks/useStore";
import { usePointerKind } from "../hooks/usePointerKind";
import { DownloadIcon, TerminalIcon } from "../ui/icons";
import type { BootMonitor } from "./BootMonitor";
import { BootGrid } from "./BootGrid";
import "./boot.css";

export interface BootScreenProps {
  readonly log: Log;
  readonly monitor: BootMonitor;
  readonly title: string;
  readonly subtitle: string;
  readonly resumeHref: string;
}

/** How many of the newest log lines the boot screen shows. */
const VISIBLE_LINES = 8;
const BOOT_VERBOSITIES: ReadonlySet<Verbosity> = new Set(["Fatal", "Error", "Warning", "Display"]);

export function BootScreen({ log, monitor, title, subtitle, resumeHref }: BootScreenProps) {
  const boot = useStore(monitor);
  const { entries } = useStore(log);
  const lines = useMemo(() => visibleLines(entries), [entries]);
  const percent = Math.round(boot.progress * 100);
  // A phone has no Esc key: a tap skips instead (App's boot dismissal takes both).
  const skipHint = usePointerKind() === "coarse" ? "Tap · skip intro" : "Esc · skip intro";

  return (
    <div className="kn-boot" data-phase={boot.phase}>
      <BootGrid />

      <div className="kn-boot__center">
        <div className="kn-boot__mark" aria-hidden="true" />
        <div className="kn-boot__titles">
          <h1 className="kn-boot__title">{title}</h1>
          <p className="kn-boot__subtitle">{subtitle}</p>
        </div>

        <div className="kn-boot__progress">
          <div
            className="kn-boot__bar"
            role="progressbar"
            aria-label="Boot progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
          >
            <div className="kn-boot__bar-fill" style={{ width: `${percent}%` }} />
          </div>
          <div className="kn-boot__status">
            <span>{boot.status}</span>
            <span className="kn-boot__percent">{percent}%</span>
          </div>
        </div>

        {boot.phase === "failed" && (
          <p className="kn-boot__error" role="alert">
            {boot.error}
          </p>
        )}
      </div>

      <section className="kn-boot__log" aria-label="Output Log">
        <h2 className="kn-boot__log-title">
          <TerminalIcon size={12} />
          Output Log
        </h2>
        <ol className="kn-boot__lines">
          {lines.map((entry, i) => (
            <LogLine key={entry.seq} entry={entry} latest={i === lines.length - 1} />
          ))}
        </ol>
      </section>

      <div className="kn-boot__actions">
        <span className="kn-boot__hint">{skipHint}</span>
        <a className="kn-boot__resume" href={resumeHref} download>
          <DownloadIcon size={14} />
          Resume.pdf
        </a>
      </div>
    </div>
  );
}

function LogLine({ entry, latest }: { entry: LogEntry; latest: boolean }) {
  return (
    <li className="kn-boot__line" data-latest={latest} data-verbosity={entry.verbosity}>
      <span className="kn-boot__time">{formatLogTime(entry.time)}</span>
      <span className="kn-boot__category">{entry.category}</span>
      <span className="kn-boot__message">{entry.message}</span>
    </li>
  );
}

function visibleLines(entries: readonly LogEntry[]): readonly LogEntry[] {
  return entries.filter((entry) => BOOT_VERBOSITIES.has(entry.verbosity)).slice(-VISIBLE_LINES);
}
